import { useEffect, useMemo, useRef, useState } from "react";
import Logo from "@/components/tpr/Logo/Logo";
import RandomWallpaper from "@/components/tpr/RandomWallpaper/RandomWallpaper";
import TprIcon from "@/components/tpr/TprIcon/TprIcon";
import SetPasswordFields from "@/components/tpr/SetPasswordFields/SetPasswordFields";
import { Button } from "@/components/ui/button";
import { Field, FieldGroup, FieldLabel, FieldSet } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Item, ItemContent } from "@/components/ui/item";
import { Spinner } from "@/components/ui/spinner";
import { login } from "@/core/auth";
import { gaPageView } from "@/core/google";
import { tprStorage } from "@/core/storage";
import { useCompletePasswordReset } from "@/hooks/useCompletePasswordReset";
import { useUpdateMemberName } from "@/hooks/useUpdateMemberName";
import { useUploadAvatar } from "@/hooks/useUploadAvatar";
import { nextGatePath } from "@/utils/loginGates";
import { useNavigate, useSearchParams } from "react-router";

const CreatePassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const token = searchParams.get("token");
  const email = searchParams.get("email") ?? "";
  const initial = useRef({
    firstName: searchParams.get("firstName") ?? "",
    lastName: searchParams.get("lastName") ?? "",
  });

  const [firstName, setFirstName] = useState(initial.current.firstName);
  const [lastName, setLastName] = useState(initial.current.lastName);
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const { mutateAsync: completeReset } = useCompletePasswordReset();
  const { mutateAsync: updateName } = useUpdateMemberName();
  const { mutateAsync: uploadAvatar } = useUploadAvatar();

  useEffect(() => {
    gaPageView("/create-password");
  }, []);

  const greetingName = useMemo(() => initial.current.firstName || "there", []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!token) {
      setError("This setup link is invalid or has expired. Please contact us for a new link.");
      return;
    }
    if (password.length < 8) {
      setError("Password must be at least 8 characters long");
      return;
    }
    if (password !== confirm) {
      setError("Passwords do not match");
      return;
    }

    setSubmitting(true);
    try {
      await completeReset({ newPassword: password, token });

      const response = await login(email, password);
      if (response instanceof Error) {
        setError(response.message);
        setSubmitting(false);
        return;
      }

      const stored = tprStorage.get("tpruser") as { id?: string } | null;
      const memberId = stored?.id;
      if (memberId) {
        const nameChanged =
          firstName !== initial.current.firstName || lastName !== initial.current.lastName;
        if (nameChanged) {
          try {
            await updateName({ memberId, firstName, lastName });
          } catch {
            // Non-fatal: the member can edit their name later in account settings.
          }
        }
        if (avatarFile) {
          try {
            await uploadAvatar({ memberId, avatarFile });
          } catch {
            // Non-fatal: the member can upload an avatar later.
          }
        }
      }

      const gate = nextGatePath(response);
      navigate(gate ?? "/login/terms", { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong. Please try again.");
      setSubmitting(false);
    }
  };

  const renderInvalid = () => (
    <Item className="border border-gray-200 bg-gray-50 dark:border-gray-700 rounded-lg p-4">
      <ItemContent>
        <div
          className="p-4 text-sm text-red-700 bg-red-100 rounded-lg dark:bg-red-200 dark:text-red-800"
          role="alert"
        >
          <p className="font-semibold">Invalid or expired link</p>
          <p className="mt-2">This setup link is invalid or has expired. Please contact us for a new link.</p>
        </div>
      </ItemContent>
    </Item>
  );

  const renderForm = () => (
    <Item className="border border-gray-200 bg-gray-50 dark:border-gray-700 rounded-lg p-4">
      <ItemContent>
        <form onSubmit={handleSubmit}>
          <div className="flex flex-col gap-2">
            {error && (
              <div
                className="p-4 mb-4 text-sm text-red-700 bg-red-100 rounded-lg dark:bg-red-200 dark:text-red-800"
                role="alert"
              >
                {error}
              </div>
            )}
            <FieldSet>
              <FieldGroup>
                <Field>
                  <FieldLabel htmlFor="firstName">First name</FieldLabel>
                  <Input
                    id="firstName"
                    type="text"
                    className="bg-white dark:text-gray-900 text-lg"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                  />
                </Field>
                <Field>
                  <FieldLabel htmlFor="lastName">Last name</FieldLabel>
                  <Input
                    id="lastName"
                    type="text"
                    className="bg-white dark:text-gray-900 text-lg"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                  />
                </Field>
                <Field>
                  <FieldLabel htmlFor="avatar">Avatar (optional)</FieldLabel>
                  <Input
                    id="avatar"
                    type="file"
                    accept="image/*"
                    className="bg-white dark:text-gray-900"
                    onChange={(e) => setAvatarFile(e.target.files?.[0] ?? null)}
                  />
                </Field>
                <SetPasswordFields
                  password={password}
                  confirm={confirm}
                  onPasswordChange={setPassword}
                  onConfirmChange={setConfirm}
                />
                <Field orientation="horizontal">
                  <Button
                    type="submit"
                    className="w-full"
                    disabled={!password || !confirm || submitting}
                    aria-label="Set password"
                  >
                    {submitting ? <Spinner className="h-4 w-4 mr-2" /> : null}
                    Set password &amp; continue
                  </Button>
                </Field>
              </FieldGroup>
            </FieldSet>
          </div>
        </form>
      </ItemContent>
    </Item>
  );

  return (
    <div className="bg-white dark:bg-gray-900">
      <div className="flex justify-center h-screen">
        <RandomWallpaper />
        <div className="flex items-center w-full max-w-md px-6 mx-auto lg:w-3/6">
          <div className="flex-1">
            <div className="text-center flex flex-col justify-center items-center">
              <div className="mb-4">
                <TprIcon size="large" />
              </div>
              <h2 className="text-4xl font-bold text-center text-gray-700 dark:text-white">
                <Logo color="black" size="large" />
              </h2>
              <p className="mt-3 text-gray-500 dark:text-gray-300">
                Welcome, {greetingName} — set your password to finish setting up your account.
              </p>
            </div>
            <div className="mt-8 w-full">{!token ? renderInvalid() : renderForm()}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreatePassword;
